import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlatformAuthProvider, usePlatformAuth } from "@/context/PlatformAuthContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import LandingPagePro from "@/pages/LandingPagePro";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Setup from "@/pages/Setup";
import SecurityPage from "@/pages/Security";
import AdminDashboard from "@/pages/admin/Dashboard";
import Departments from "@/pages/admin/Departments";
import DepartmentDetail from "@/pages/admin/DepartmentDetail";
import Rules from "@/pages/admin/Rules";
import Approvals from "@/pages/admin/Approvals";
import Transactions from "@/pages/admin/Transactions";
import Reports from "@/pages/admin/Reports";
import Users from "@/pages/admin/Users";
import Security from "@/pages/admin/Security";
import Billing from "@/pages/admin/Billing";
import DeptDashboard from "@/pages/department/Dashboard";
import InitiatePayment from "@/pages/department/InitiatePayment";
import MyPayments from "@/pages/department/MyPayments";
import Beneficiaries from "@/pages/department/Beneficiaries";
import MyRules from "@/pages/department/MyRules";
import DeptSecurity from "@/pages/department/Security";

// Platform Admin Pages
import PlatformLogin from "@/pages/platform/Login";
import PlatformDashboard from "@/pages/platform/Dashboard";
import PlatformCustomers from "@/pages/platform/Customers";
import PlatformSalesReps from "@/pages/platform/SalesReps";
import PlatformCoupons from "@/pages/platform/Coupons";
import PlatformEnterprise from "@/pages/platform/Enterprise";
import PlatformUsers from "@/pages/platform/Users";
import PlatformRevenue from "@/pages/platform/Revenue";
import PlatformSettings from "@/pages/platform/Settings";
import PlatformSecurityMonitoring from "@/pages/platform/SecurityMonitoring";
import PlatformInquiries from "@/pages/platform/Inquiries";

// Admin Pages (Organization)
import AuditLogs from "@/pages/admin/AuditLogs";
import AdminInitiatePayment from "@/pages/admin/InitiatePayment";
import AdminBeneficiaries from "@/pages/admin/Beneficiaries";
import Banking from "@/pages/admin/Banking";

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
      <p className="text-gray-500 font-medium">Loading FinOrchest...</p>
    </div>
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children, requireAdmin = false, requireSetup = true }) => {
  const { user, loading, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if setup is required
  if (requireSetup && !user?.organization_id) {
    return <Navigate to="/setup" replace />;
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/department" replace />;
  }

  return children;
};

// Platform protected route wrapper is defined inside PlatformRouter to access PlatformAuthProvider context

// Route based on user role
const RoleBasedRedirect = () => {
  const { user, loading, isAuthenticated, isAdmin } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.organization_id) {
    return <Navigate to="/setup" replace />;
  }

  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/department" replace />;
};

// Main app router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL hash (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPagePro />} />
      <Route path="/home" element={<Landing />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Setup route */}
      <Route path="/setup" element={
        <ProtectedRoute requireSetup={false}>
          <Setup />
        </ProtectedRoute>
      } />

      {/* Role-based redirect */}
      <Route path="/dashboard" element={<RoleBasedRedirect />} />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/departments" element={
        <ProtectedRoute requireAdmin>
          <Departments />
        </ProtectedRoute>
      } />
      <Route path="/admin/departments/:id" element={
        <ProtectedRoute requireAdmin>
          <DepartmentDetail />
        </ProtectedRoute>
      } />
      <Route path="/admin/rules" element={
        <ProtectedRoute requireAdmin>
          <Rules />
        </ProtectedRoute>
      } />
      <Route path="/admin/approvals" element={
        <ProtectedRoute requireAdmin>
          <Approvals />
        </ProtectedRoute>
      } />
      <Route path="/admin/transactions" element={
        <ProtectedRoute requireAdmin>
          <Transactions />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute requireAdmin>
          <Reports />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute requireAdmin>
          <Users />
        </ProtectedRoute>
      } />
      <Route path="/admin/security" element={
        <ProtectedRoute requireAdmin>
          <Security />
        </ProtectedRoute>
      } />
      <Route path="/admin/billing" element={
        <ProtectedRoute requireAdmin>
          <Billing />
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute requireAdmin>
          <AuditLogs />
        </ProtectedRoute>
      } />
      <Route path="/admin/initiate-payment" element={
        <ProtectedRoute requireAdmin>
          <AdminInitiatePayment />
        </ProtectedRoute>
      } />
      <Route path="/admin/beneficiaries" element={
        <ProtectedRoute requireAdmin>
          <AdminBeneficiaries />
        </ProtectedRoute>
      } />
      <Route path="/admin/banking" element={
        <ProtectedRoute requireAdmin>
          <Banking />
        </ProtectedRoute>
      } />

      {/* Department Head routes */}
      <Route path="/department" element={
        <ProtectedRoute>
          <DeptDashboard />
        </ProtectedRoute>
      } />
      <Route path="/department/payment" element={
        <ProtectedRoute>
          <InitiatePayment />
        </ProtectedRoute>
      } />
      <Route path="/department/payments" element={
        <ProtectedRoute>
          <MyPayments />
        </ProtectedRoute>
      } />
      <Route path="/department/beneficiaries" element={
        <ProtectedRoute>
          <Beneficiaries />
        </ProtectedRoute>
      } />
      <Route path="/department/rules" element={
        <ProtectedRoute>
          <MyRules />
        </ProtectedRoute>
      } />
      <Route path="/department/security" element={
        <ProtectedRoute>
          <DeptSecurity />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Platform Admin Router (separate from main app)
function PlatformRouter() {
  return (
    <PlatformAuthProvider>
      <PlatformRoutesWithAuth />
    </PlatformAuthProvider>
  );
}

// Routes component that uses the auth context - must be inside PlatformAuthProvider
function PlatformRoutesWithAuth() {
  const { loading, isAuthenticated } = usePlatformAuth();
  const location = useLocation();

  // Platform protected route wrapper
  const PlatformProtectedRoute = ({ children }) => {
    if (loading) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <Navigate to="/platform/login" state={{ from: location }} replace />;
    }

    return children;
  };

  return (
    <Routes>
      <Route path="login" element={<PlatformLogin />} />
      <Route path="" element={
        <PlatformProtectedRoute>
          <PlatformDashboard />
        </PlatformProtectedRoute>
      } />
      <Route path="customers" element={
        <PlatformProtectedRoute>
          <PlatformCustomers />
        </PlatformProtectedRoute>
      } />
      <Route path="inquiries" element={
        <PlatformProtectedRoute>
          <PlatformInquiries />
        </PlatformProtectedRoute>
      } />
      <Route path="sales-reps" element={
        <PlatformProtectedRoute>
          <PlatformSalesReps />
        </PlatformProtectedRoute>
      } />
      <Route path="coupons" element={
        <PlatformProtectedRoute>
          <PlatformCoupons />
        </PlatformProtectedRoute>
      } />
      <Route path="enterprise" element={
        <PlatformProtectedRoute>
          <PlatformEnterprise />
        </PlatformProtectedRoute>
      } />
      <Route path="users" element={
        <PlatformProtectedRoute>
          <PlatformUsers />
        </PlatformProtectedRoute>
      } />
      <Route path="revenue" element={
        <PlatformProtectedRoute>
          <PlatformRevenue />
        </PlatformProtectedRoute>
      } />
      <Route path="settings" element={
        <PlatformProtectedRoute>
          <PlatformSettings />
        </PlatformProtectedRoute>
      } />
      <Route path="security" element={
        <PlatformProtectedRoute>
          <PlatformSecurityMonitoring />
        </PlatformProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/platform" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Platform Admin Routes (separate auth context) */}
          <Route path="/platform/*" element={<PlatformRouter />} />
          
          {/* Main App Routes */}
          <Route path="/*" element={
            <AuthProvider>
              <AppRouter />
            </AuthProvider>
          } />
        </Routes>
        <Toaster 
          theme="light" 
          position="top-right"
          toastOptions={{
            style: {
              background: '#FFFFFF',
              border: '1px solid #E5E7EB',
              color: '#111827'
            }
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
